import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ArrowLeft, Download, FolderGit2, History, Inbox, Paperclip, Plus, Sparkles } from 'lucide-react';
import RecordHistoryDrawer from '../../components/Audit/RecordHistoryDrawer';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import FormSection from '../../components/Common/FormSection';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import EntityAutocomplete from '../../components/Common/EntityAutocomplete';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { identityService, institutionService, legislationService, licenseService, processService } from '../../services/api';
import InteractiveMap from '../../components/Map/InteractiveMap';
import {
  ANALISE_CHECKLIST,
  PARECER_TEMPLATES,
  inferLicenseKind,
  serializeAnaliseChecklist,
} from '../../constants/parecerTemplates';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { useLookupMaps } from '../../hooks/useLookupMaps';
import { shortId } from '../../utils/lookupLabels';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { useTenant } from '../../contexts/TenantContext';
import { isConsultoriaTenant, isRequesterRole } from '../../utils/tenantProfile';
import { useAuth } from '../../contexts/AuthContext';
import ProcessoDespachoSection from './ProcessoDespachoSection';
import ProcessoAutorizacaoSection from './ProcessoAutorizacaoSection';
import ExigenciaRespostaModal from '../../components/Process/ExigenciaRespostaModal';
import {
  processDetailActions,
  showAnswerAction,
  showDocumentsAnswerWarning,
  showExpiredNotice,
} from '../../utils/processDetailActions';

const SIT_PROC = { 1: 'sitOpen', 2: 'sitProgress', 3: 'sitDone', 4: 'sitArchived' };
const PROCESSO_NEXT = { 1: [2, 4], 2: [3, 4], 3: [4], 4: [] };
const TRANSITION_LABEL = { 2: 'Enviar para análise', 3: 'Concluir processo', 4: 'Arquivar processo' };

function documentoBaixavel(arquivo) {
  const ref = String(arquivo || '');
  if (!ref || ref.startsWith('PROTOCOLO:') || ref.startsWith('http') || ref.startsWith('CHECKLIST')) return false;
  return ref.includes('/');
}
const SIT_MOVE = { 1: 'sitPending', 2: 'sitReceived', 3: 'sitForwarded', 4: 'sitReturned' };
const TIPO = { 1: 'Ambiental', 2: 'Alvará', 3: 'Habite-se', 4: 'Diversos' };

const emptyMove = {
  orgao_origem_id: '',
  setor_origem_id: '',
  responsavel_origem_id: '',
  orgao_destino_id: '',
  setor_destino_id: '',
  responsavel_destino_id: '',
  parecer: '',
  tipo_midia_id: '',
  exigencia: false,
  prazo_dias: 15,
};

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

function formatWhen(value) {
  if (!value) return '—';
  const s = String(value);
  return s.length >= 16 ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : s.slice(0, 10);
}

const ProcessoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('process');
  const { tenant } = useTenant();
  const { user } = useAuth();
  const consultoria = isConsultoriaTenant(tenant);
  const requester = isRequesterRole(user);
  const actions = processDetailActions({ requester, consultoria });
  const { personMap } = useLookupMaps({ persons: true });
  const queryClient = useQueryClient();
  const [showMove, setShowMove] = useState(false);
  const [move, setMove] = useState(emptyMove);
  const [gerandoParecer, setGerandoParecer] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docTipo, setDocTipo] = useState('');
  const [analiseChecks, setAnaliseChecks] = useState({});
  const [taxa, setTaxa] = useState({ descricao: 'Taxa ambiental', valor: '', invoice_url: '' });
  const [analiseExig, setAnaliseExig] = useState({ item: null, texto: '', deferida: true });
  const [respostaExig, setRespostaExig] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data: processo, isLoading, error } = useQuery(
    ['processo', id],
    () => processService.getProcesso(id).then((r) => r.data),
    { enabled: !!id }
  );
  const { data: movesRaw } = useQuery(
    ['tramitacoes', id],
    () => processService.getTramitacoesByProcesso(id).then((r) => r.data),
    { enabled: !!id }
  );
  const { data: docsRaw } = useQuery(
    ['documentos-processo', id],
    () => processService.getDocumentosByProcesso(id).then((r) => r.data),
    { enabled: !!id }
  );
  const { data: taxasRaw } = useQuery(
    ['taxas-processo', id],
    () => processService.listTaxasByProcesso(id).then((r) => r.data),
    { enabled: !!id }
  );
  const taxas = unwrap(taxasRaw);
  const { data: orgaosRaw } = useQuery(['orgaos'], () =>
    institutionService.orgaoCrud.getList({ limit: 500 })
  );
  const { data: setoresRaw } = useQuery(['setores'], () =>
    institutionService.setorCrud.getList({ limit: 1000 })
  );
  const { data: servidoresRaw } = useQuery(['servidores'], () =>
    institutionService.servidorCrud.getList({ limit: 1000 })
  );
  const { data: midiasRaw } = useQuery(['tipos-midia'], () =>
    processService.tipoMidiaCrud.getList({ limit: 200 })
  );
  const { data: tipoLicenca } = useQuery(
    ['tipo-licenca', processo?.tipo_licenca_id],
    () => licenseService.getTipoLicenca(processo.tipo_licenca_id).then((r) => r.data),
    { enabled: Boolean(processo?.tipo_licenca_id) }
  );
  const { data: licencaDoProcesso, error: licencaError } = useQuery(
    ['licenca-by-processo', id],
    () =>
      licenseService.getLicencaByProcesso(id).then((r) => r.data).catch((err) => {
        if (err?.response?.status === 404) return null;
        throw err;
      }),
    { enabled: !!id }
  );
  const licencaProc = Array.isArray(licencaDoProcesso) ? licencaDoProcesso[0] : licencaDoProcesso;
  const { data: empreendimentoProc } = useQuery(
    ['enterprise-processo', licencaProc?.empreendimento_id],
    () => identityService.getEnterprise(licencaProc.empreendimento_id).then((r) => r.data),
    { enabled: Boolean(licencaProc?.empreendimento_id) }
  );

  const moves = unwrap(movesRaw).slice().sort((a, b) =>
    String(b.data_entrada || '').localeCompare(String(a.data_entrada || ''))
  );
  const docs = unwrap(docsRaw);
  const { data: versionsRaw } = useQuery(
    ['documento-versoes', id, docs.map((d) => d.id).join(',')],
    async () => {
      const lists = await Promise.all(
        docs.slice(0, 40).map((doc) =>
          processService
            .getDocumentoVersions(doc.id)
            .then((r) => unwrap(r.data).map((v) => ({ ...v, documento_id: doc.id })))
        )
      );
      return lists.flat();
    },
    { enabled: docs.length > 0 }
  );
  const versions = unwrap(versionsRaw);
  const orgaos = unwrap(orgaosRaw);
  const setores = unwrap(setoresRaw);
  const servidores = unwrap(servidoresRaw);
  const midias = unwrap(midiasRaw);
  const licenseKind = inferLicenseKind(tipoLicenca?.nome || tipoLicenca?.name || processo?.tipo);
  const analiseItems = ANALISE_CHECKLIST[licenseKind] || ANALISE_CHECKLIST.LP;
  const parecerTemplate = PARECER_TEMPLATES[licenseKind] || PARECER_TEMPLATES.LP;

  useEffect(() => {
    if (consultoria || !processo || searchParams.get('tramitar') !== '1') return;
    setMove({
      ...emptyMove,
      orgao_origem_id: processo.orgao_origem_id || '',
      setor_origem_id: processo.setor_origem_id || '',
      responsavel_origem_id: processo.usuario_id || '',
      parecer: parecerTemplate,
    });
    setShowMove(true);
    const next = new URLSearchParams(searchParams);
    next.delete('tramitar');
    setSearchParams(next, { replace: true });
  }, [consultoria, parecerTemplate, processo, searchParams, setSearchParams]);

  const gerarParecerIa = async () => {
    setGerandoParecer(true);
    try {
      const { data } = await legislationService.gerarParecer({
        kind: licenseKind,
        processo_numero: processo?.numero,
        empreendimento: empreendimentoProc?.name || empreendimentoProc?.nome,
        descricao: processo?.observacao || processo?.descricao,
        fallback_template: parecerTemplate,
      });
      setMove((f) => ({ ...f, parecer: data?.parecer || parecerTemplate }));
      if (data?.source === 'llm') toast.success('Parecer gerado com IA');
      else toast('Modelo de parecer aplicado (IA indisponível)', { icon: 'ℹ️' });
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setGerandoParecer(false);
    }
  };

  const orgaoName = (oid) => orgaos.find((o) => o.id === oid)?.name || oid || '—';
  const setorName = (sid) => setores.find((s) => s.id === sid)?.name || sid || '—';
  const midiaName = (mid) => midias.find((m) => m.id === mid)?.tipo || mid || '—';

  const orgaoOptions = useMemo(
    () => orgaos.filter((o) => o.receive_routing !== false).map((o) => ({ id: o.id, label: o.name || o.id })),
    [orgaos]
  );
  const midiaOptions = useMemo(
    () => midias.map((m) => ({ id: m.id, label: m.tipo || m.id })),
    [midias]
  );
  const setorOptions = (orgaoId, { routingOnly } = {}) =>
    setores
      .filter((s) => !orgaoId || s.orgao_id === orgaoId)
      .filter((s) => !routingOnly || s.receive_routing !== false)
      .map((s) => ({ id: s.id, label: s.name || s.id }));
  const servidorOptions = (setorId, { routingOnly } = {}) =>
    servidores
      .filter((s) => !setorId || s.setor_id === setorId)
      .filter((s) => !routingOnly || s.receive_routing !== false)
      .map((s) => ({
        id: s.id,
        label: personMap[String(s.person_id || '')] || s.name || s.matricula || shortId(s.id),
      }));

  const invalidate = () => {
    queryClient.invalidateQueries(['tramitacoes', id]);
    queryClient.invalidateQueries(['documentos-processo', id]);
    queryClient.invalidateQueries(['taxas-processo', id]);
    queryClient.invalidateQueries(['processo', id]);
    queryClient.invalidateQueries(['process-inbox']);
  };

  const analisarExigencia = useMutation(
    ({ item, texto, deferida }) => processService.analisarExigencia(item.id, { texto, deferida }),
    {
      onSuccess: () => {
        toast.success('Exigência analisada');
        setAnaliseExig({ item: null, texto: '', deferida: true });
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const createTaxa = useMutation(
    (payload) => processService.createTaxa(payload),
    {
      onSuccess: () => {
        toast.success('Taxa emitida');
        setTaxa({ descricao: 'Taxa ambiental', valor: '', invoice_url: '' });
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const createMove = useMutation((payload) => processService.createTramitacao(payload), {
    onSuccess: () => {
      toast.success('Tramitação registrada');
      setShowMove(false);
      setMove(emptyMove);
      invalidate();
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const receiveMove = useMutation((item) =>
    processService.updateTramitacao(item.id, {
      situacao: 2,
      data_recebimento: new Date().toISOString(),
    })
  , {
    onSuccess: () => {
      toast.success(t('receive'));
      invalidate();
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const updateMoveSit = useMutation(
    ({ item, situacao }) => processService.updateTramitacao(item.id, { situacao }),
    {
      onSuccess: (_, vars) => {
        toast.success(vars.situacao === 3 ? 'Encaminhada' : 'Devolvida');
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const addDoc = useMutation(
    () => processService.uploadDocumentoProcesso(id, docTipo, docFile),
    {
      onSuccess: () => {
        toast.success(t('addDocument'));
        setDocFile(null);
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const saveChecklist = useMutation(async () => {
    const tipoId = docTipo || midias[0]?.id;
    if (!tipoId) throw new Error('Cadastre um tipo de mídia para gravar o checklist.');
    const texto = serializeAnaliseChecklist(licenseKind, analiseChecks);
    const { data: doc } = await processService.createDocumentoProcesso({
      processo_id: id,
      tipo_documento_id: tipoId,
      arquivo: `CHECKLIST:${licenseKind}`,
    });
    await processService.createDocumentoVersion({
      documento_processo_id: doc.id,
      informacao: texto,
      arquivo: `CHECKLIST:${licenseKind}`,
    });
  }, {
    onSuccess: () => {
      toast.success('Checklist de análise gravado no expediente');
      invalidate();
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const removeDoc = useMutation((docId) => processService.deleteDocumentoProcesso(docId), {
    onSuccess: () => {
      toast.success('Documento removido');
      invalidate();
    },
    onError: (e) => toast.error(userFacingError(e, 'deleteError')),
  });

  const submitMove = (event) => {
    event.preventDefault();
    if (
      !move.orgao_origem_id ||
      !move.setor_origem_id ||
      !move.responsavel_origem_id ||
      !move.orgao_destino_id ||
      !move.setor_destino_id ||
      !move.responsavel_destino_id
    ) {
      toast.error(t('fillLinks'));
      return;
    }
    createMove.mutate({
      processo_id: id,
      tramitacao_anterior_id: moves[0]?.id || null,
      situacao: 1,
      orgao_origem_id: move.orgao_origem_id,
      setor_origem_id: move.setor_origem_id,
      responsavel_origem_id: move.responsavel_origem_id,
      parecer: move.parecer || null,
      exigencia: Boolean(move.exigencia),
      orgao_destino_id: move.orgao_destino_id,
      setor_destino_id: move.setor_destino_id,
      responsavel_destino_id: move.responsavel_destino_id,
      tipo_midia_id: move.tipo_midia_id || null,
      prazo_dias: Number(move.prazo_dias) || 15,
    });
  };

  if (isLoading) {
    return (
      <FormPageShell>
        <p className="text-gray-500">Carregando...</p>
      </FormPageShell>
    );
  }
  if (error || !processo) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
          <button type="button" className="btn btn-outline mt-3" onClick={() => navigate('/process/cases')}>
            {t('title')}
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t('detailTitle', { number: processo.numero || id })}
          description={`${TIPO[processo.tipo] || processo.tipo} · ${t(SIT_PROC[processo.situacao] || 'sitOpen')}`}
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: t('title'), to: '/process/cases' },
            { label: processo.numero || id },
          ]}
        >
          <button
            type="button"
            className="btn btn-outline inline-flex items-center gap-2"
            onClick={() => setShowHistory(true)}
          >
            <History className="h-4 w-4" />
            Histórico
          </button>
          <button
            type="button"
            className="btn btn-outline inline-flex items-center gap-2"
            onClick={async () => {
              try {
                const { data } = await processService.downloadProcessoPdf(id);
                const url = URL.createObjectURL(data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${processo.numero || 'processo'}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(userFacingError(e, 'loadError'));
              }
            }}
          >
            <Download className="h-4 w-4" />
            {t('downloadProcessPdf')}
          </button>
          {consultoria || requester ? null : (
            <Link to="/process/routing" className="btn btn-outline inline-flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              {t('inboxTitle')}
            </Link>
          )}
          {requester ? null : (
          <Can permission={P.PROCESS_ACCESS}>
            {(PROCESSO_NEXT[processo.situacao] || []).map((target) => (
              <button
                key={target}
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  const label = TRANSITION_LABEL[target];
                  processService
                    .transitionProcesso(id, target, window.prompt(`${label}. Parecer (opcional)`, parecerTemplate) || '')
                    .then(() => {
                      toast.success(label);
                      queryClient.invalidateQueries(['processo', id]);
                    })
                    .catch((e) => toast.error(userFacingError(e, 'saveError')));
                }}
              >
                {TRANSITION_LABEL[target]}
              </button>
            ))}
            {consultoria ? null : (
              <button
                type="button"
                className="btn btn-primary inline-flex items-center gap-2"
                onClick={() => {
                  setMove({
                    ...emptyMove,
                    orgao_origem_id: processo.orgao_origem_id || '',
                    setor_origem_id: processo.setor_origem_id || '',
                    responsavel_origem_id: processo.usuario_id || '',
                    parecer: parecerTemplate,
                  });
                  setShowMove(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('newMove')}
              </button>
            )}
          </Can>
          )}
        </PageHeader>
        {licencaError ? <QueryErrorBanner error={licencaError} /> : null}

        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-primary-700"
          onClick={() => navigate('/process/cases')}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('title')}
        </button>

        <FormSection title={t('common:identify')} icon={FolderGit2} variant="primary">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-gray-500">Número</dt>
              <dd className="text-sm text-gray-900">{processo.numero}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Situação</dt>
              <dd className="text-sm text-gray-900">{t(SIT_PROC[processo.situacao] || 'sitOpen')}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">{t('origin')}</dt>
              <dd className="text-sm text-gray-900">
                {orgaoName(processo.orgao_origem_id)} · {setorName(processo.setor_origem_id)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Abertura</dt>
              <dd className="text-sm text-gray-900">{formatWhen(processo.data_abertura)}</dd>
            </div>
          </dl>
        </FormSection>

        <ProcessoDespachoSection
          processoId={id}
          processoNumero={processo.numero}
          requester={requester}
        />

        {requester ? null : <ProcessoAutorizacaoSection processoId={id} />}

        {empreendimentoProc?.latitude != null && empreendimentoProc?.longitude != null ? (
          <FormSection title="Local do empreendimento" icon={FolderGit2} variant="slate">
            <p className="mb-3 text-sm text-gray-600">
              {empreendimentoProc.name || empreendimentoProc.nome || 'Empreendimento'}
              {empreendimentoProc.city ? ` · ${empreendimentoProc.city}` : ''}
            </p>
            <InteractiveMap
              enterprises={[
                {
                  id: empreendimentoProc.id,
                  name: empreendimentoProc.name || empreendimentoProc.nome,
                  type: (empreendimentoProc.type || 'default').toString().toLowerCase(),
                  is_active: empreendimentoProc.is_active !== false,
                  latitude: Number(empreendimentoProc.latitude),
                  longitude: Number(empreendimentoProc.longitude),
                },
              ]}
              center={[Number(empreendimentoProc.longitude), Number(empreendimentoProc.latitude)]}
              zoom={14}
              height="280px"
              mapClassName="rounded-xl border-gray-200"
            />
          </FormSection>
        ) : null}

        <FormSection title={t('timeline')} icon={Inbox} variant="slate">
          {moves.length === 0 ? (
            <p className="text-sm text-gray-500">{t('emptyTimeline')}</p>
          ) : (
            <ol className="space-y-4">
              {moves.map((item) => (
                <li key={item.id} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
                      {t(SIT_MOVE[item.situacao] || 'sitPending')}
                    </span>
                    {item.is_exigencia || item.exigencia ? (
                      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        Exigência {item.exigencia_status ? `· ${item.exigencia_status}` : ''}
                      </span>
                    ) : null}
                    <span className="text-xs text-gray-500">
                      {formatWhen(item.data_entrada)}
                      {item.prazo_dias ? ` · ${item.prazo_dias} dia(s)` : ''}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-800">
                    {orgaoName(item.orgao_origem_id)} → {orgaoName(item.orgao_destino_id)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {setorName(item.setor_origem_id)} → {setorName(item.setor_destino_id)}
                    {item.tipo_midia_id ? ` · ${midiaName(item.tipo_midia_id)}` : ''}
                  </p>
                  {item.parecer ? (
                    <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{item.parecer}</p>
                  ) : null}
                  {item.resposta_texto ? (
                    <p className="mt-2 text-sm text-gray-700">Resposta do requerente: {item.resposta_texto}</p>
                  ) : null}
                  {item.analise_texto ? (
                    <p className="mt-2 text-sm text-gray-700">Análise: {item.analise_texto}</p>
                  ) : null}
                  {showAnswerAction({ requester, move: item }) ? (
                    <button
                      type="button"
                      className="btn btn-primary mt-3"
                      onClick={() => setRespostaExig(item)}
                    >
                      Responder exigência
                    </button>
                  ) : null}
                  {showExpiredNotice({ requester, move: item }) ? (
                    <p className="mt-2 text-sm text-amber-800">
                      Prazo da exigência vencido. Peça ao órgão a prorrogação do prazo.
                    </p>
                  ) : null}
                  {actions.analyzeExigencia && item.is_exigencia && item.exigencia_status === 'respondida' ? (
                    <Can permission={P.PROCESS_ACCESS}>
                      <button
                        type="button"
                        className="btn btn-outline mt-2"
                        onClick={() => setAnaliseExig({ item, texto: '', deferida: true })}
                      >
                        Analisar exigência
                      </button>
                    </Can>
                  ) : null}
                  {!actions.routeMove ? null : (
                    <Can permission={P.PROCESS_ACCESS}>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.situacao === 1 ? (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => receiveMove.mutate(item)}
                          >
                            {t('receive')}
                          </button>
                        ) : null}
                        {item.situacao === 2 ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => updateMoveSit.mutate({ item, situacao: 3 })}
                            >
                              Encaminhar
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline"
                              onClick={() => updateMoveSit.mutate({ item, situacao: 4 })}
                            >
                              Devolver
                            </button>
                          </>
                        ) : null}
                      </div>
                    </Can>
                  )}
                </li>
              ))}
            </ol>
          )}
        </FormSection>

        <FormSection title="Taxas do processo" icon={Inbox} variant="slate">
          {taxas.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma taxa emitida.</p>
          ) : (
            <ul className="mb-4 divide-y rounded-lg border">
              {taxas.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span>
                    {row.descricao} · R$ {Number(row.valor || 0).toFixed(2)}
                    <span className="ml-2 text-xs text-gray-500">{row.status}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {row.status === 'paga' ? (
                      <span className="text-xs text-emerald-700">Paga</span>
                    ) : row.invoice_url ? (
                      <a href={row.invoice_url} target="_blank" rel="noreferrer" className="text-primary-700">
                        Boleto
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">Sem boleto</span>
                    )}
                    {actions.manageTaxes && row.status !== 'paga' ? (
                      <button
                        type="button"
                        className="text-primary-700"
                        onClick={() =>
                          processService
                            .updateTaxa(row.id, { status: 'paga' })
                            .then(() => {
                              toast.success('Taxa marcada como paga');
                              queryClient.invalidateQueries(['taxas-processo', id]);
                            })
                            .catch((e) => toast.error(userFacingError(e, 'saveError')))
                        }
                      >
                        Marcar como paga
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {actions.manageTaxes ? (
          <Can permission={P.PROCESS_ACCESS}>
            <div className="grid gap-3 sm:grid-cols-3">
              <input
                className="form-input"
                placeholder="Descrição"
                value={taxa.descricao}
                onChange={(e) => setTaxa((f) => ({ ...f, descricao: e.target.value }))}
              />
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Valor"
                value={taxa.valor}
                onChange={(e) => setTaxa((f) => ({ ...f, valor: e.target.value }))}
              />
              <input
                className="form-input"
                placeholder="URL do boleto (opcional)"
                value={taxa.invoice_url}
                onChange={(e) => setTaxa((f) => ({ ...f, invoice_url: e.target.value }))}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline mt-3"
              disabled={!taxa.descricao || !taxa.valor || createTaxa.isLoading}
              onClick={() =>
                createTaxa.mutate({
                  processo_id: id,
                  descricao: taxa.descricao,
                  valor: Number(taxa.valor),
                  invoice_url: taxa.invoice_url || null,
                })
              }
            >
              Emitir taxa
            </button>
          </Can>
          ) : null}
        </FormSection>

        {actions.analysisChecklist ? (
        <FormSection title={`Checklist de análise (${licenseKind})`} icon={Inbox} variant="slate">
          <p className="mb-3 text-sm text-gray-600">
            Itens CONAMA / tipologia para o gabinete. Grave no expediente quando concluir a conferência.
          </p>
          <ul className="space-y-2">
            {analiseItems.map((label) => (
              <li key={label}>
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(analiseChecks[label])}
                    onChange={(e) =>
                      setAnaliseChecks((prev) => ({ ...prev, [label]: e.target.checked }))
                    }
                  />
                  <span>{label}</span>
                </label>
              </li>
            ))}
          </ul>
          <Can permission={P.PROCESS_ACCESS}>
            <button
              type="button"
              className="btn btn-outline mt-4"
              disabled={saveChecklist.isLoading}
              onClick={() => saveChecklist.mutate()}
            >
              Gravar checklist no expediente
            </button>
          </Can>
        </FormSection>
        ) : null}

        <FormSection title={t('documents')} icon={Paperclip} variant="emerald">
          {showDocumentsAnswerWarning({ requester, moves }) ? (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p>
                Este processo tem exigência aberta. Anexar aqui não responde a exigência: use Responder exigência
                na tramitação, onde também dá para enviar o arquivo.
              </p>
              <button
                type="button"
                className="btn btn-outline mt-2"
                onClick={() => setRespostaExig(moves.find((m) => showAnswerAction({ requester, move: m })) || null)}
                disabled={!moves.some((m) => showAnswerAction({ requester, move: m }))}
              >
                Responder exigência
              </button>
            </div>
          ) : null}
          <Can permission={P.PROCESS_ACCESS}>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <EntityAutocomplete
                id="doc-tipo"
                label="Tipo"
                value={docTipo}
                onChange={setDocTipo}
                options={midiaOptions}
                placeholder="Tipo de mídia…"
              />
              <div className="form-group sm:col-span-2">
                <label htmlFor="doc-file" className="form-label">
                  {t('fileLabel')}
                </label>
                <div className="flex gap-2">
                  <input
                    id="doc-file"
                    key={docFile ? docFile.name : 'sem-arquivo'}
                    type="file"
                    className="form-input"
                    onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!docTipo || !docFile || addDoc.isLoading}
                    onClick={() => addDoc.mutate()}
                  >
                    {t('addDocument')}
                  </button>
                </div>
              </div>
            </div>
          </Can>
          {docs.length === 0 ? (
            <p className="text-sm text-gray-500">{t('emptyDocs')}</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
              {docs.map((doc) => (
                <li key={doc.id} className="px-4 py-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span>
                      {(String(doc.arquivo || '').split('/').pop() || '—')} · {midiaName(doc.tipo_documento_id)}
                    </span>
                    <div className="flex items-center gap-3">
                      {String(doc.arquivo || '').startsWith('http') ? (
                        <a href={doc.arquivo} target="_blank" rel="noreferrer" className="text-primary-700">
                          Abrir
                        </a>
                      ) : null}
                      {documentoBaixavel(doc.arquivo) ? (
                        <button
                          type="button"
                          className="text-primary-700"
                          onClick={async () => {
                            try {
                              const { data } = await processService.downloadDocumentoFile(doc.id);
                              const url = URL.createObjectURL(data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = String(doc.arquivo).split('/').pop() || 'documento.pdf';
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              toast.error(userFacingError(e, 'loadError'));
                            }
                          }}
                        >
                          Baixar
                        </button>
                      ) : null}
                      {actions.removeDocument ? (
                        <Can permission={P.PROCESS_ACCESS}>
                          <button
                            type="button"
                            className="text-red-600"
                            onClick={() => removeDoc.mutate(doc.id)}
                          >
                            Remover
                          </button>
                        </Can>
                      ) : null}
                    </div>
                  </div>
                  {versions.filter((v) => v.documento_id === doc.id).length > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs text-gray-500">
                      {versions
                        .filter((v) => v.documento_id === doc.id)
                        .map((v) => (
                          <li key={v.id}>
                            v{formatWhen(v.data)} · {v.informacao || v.arquivo || 'versão'}
                          </li>
                        ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </FormSection>
      </div>

      {showMove && (
        <FormModalLayout
          title={t('newMove')}
          subtitle={`${t('origin')} → ${t('destination')}`}
          headerIcon={Inbox}
          onClose={() => setShowMove(false)}
          size="lg"
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setShowMove(false)}>
                Cancelar
              </button>
              <button type="submit" form="tramitacao-form" className="btn btn-primary">
                {t('forward')}
              </button>
            </>
          }
        >
          <form id="tramitacao-form" onSubmit={submitMove} className="space-y-4">
            <EntityAutocomplete
              id="move-org-orig"
              label={`${t('origin')} — órgão`}
              value={move.orgao_origem_id}
              onChange={(oid) =>
                setMove((f) => ({ ...f, orgao_origem_id: oid, setor_origem_id: '', responsavel_origem_id: '' }))
              }
              options={orgaoOptions}
            />
            <EntityAutocomplete
              id="move-setor-orig"
              label={`${t('origin')} — setor`}
              value={move.setor_origem_id}
              onChange={(sid) => setMove((f) => ({ ...f, setor_origem_id: sid, responsavel_origem_id: '' }))}
              options={setorOptions(move.orgao_origem_id)}
              disabled={!move.orgao_origem_id}
            />
            <EntityAutocomplete
              id="move-resp-orig"
              label={`${t('origin')} — responsável`}
              value={move.responsavel_origem_id}
              onChange={(rid) => setMove((f) => ({ ...f, responsavel_origem_id: rid }))}
              options={servidorOptions(move.setor_origem_id)}
              disabled={!move.setor_origem_id}
            />
            <EntityAutocomplete
              id="move-org-dest"
              label={`${t('destination')} — órgão`}
              value={move.orgao_destino_id}
              onChange={(oid) =>
                setMove((f) => ({ ...f, orgao_destino_id: oid, setor_destino_id: '', responsavel_destino_id: '' }))
              }
              options={orgaoOptions}
            />
            <EntityAutocomplete
              id="move-setor-dest"
              label={`${t('destination')} — setor`}
              value={move.setor_destino_id}
              onChange={(sid) => setMove((f) => ({ ...f, setor_destino_id: sid, responsavel_destino_id: '' }))}
              options={setorOptions(move.orgao_destino_id, { routingOnly: true })}
              disabled={!move.orgao_destino_id}
            />
            <EntityAutocomplete
              id="move-resp-dest"
              label={`${t('destination')} — responsável`}
              value={move.responsavel_destino_id}
              onChange={(rid) => setMove((f) => ({ ...f, responsavel_destino_id: rid }))}
              options={servidorOptions(move.setor_destino_id, { routingOnly: true })}
              disabled={!move.setor_destino_id}
            />
            <div className="form-group">
              <label htmlFor="move-prazo" className="form-label">
                Prazo de análise (dias)
              </label>
              <input
                id="move-prazo"
                type="number"
                min={1}
                className="form-input"
                value={move.prazo_dias}
                onChange={(e) => setMove((f) => ({ ...f, prazo_dias: e.target.value }))}
              />
            </div>
            <EntityAutocomplete
              id="move-midia"
              label="Mídia"
              value={move.tipo_midia_id}
              onChange={(mid) => setMove((f) => ({ ...f, tipo_midia_id: mid }))}
              options={midiaOptions}
            />
            <div className="form-group">
              <div className="mb-1 flex items-center justify-between gap-2">
                <label htmlFor="move-parecer" className="form-label mb-0">
                  {t('parecer')} ({licenseKind})
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700"
                    onClick={gerarParecerIa}
                    disabled={gerandoParecer}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {gerandoParecer ? 'Gerando…' : 'Gerar com IA'}
                  </button>
                  <button
                    type="button"
                    className="text-xs font-medium text-primary-700"
                    onClick={() => setMove((f) => ({ ...f, parecer: parecerTemplate }))}
                  >
                    Aplicar template
                  </button>
                </div>
              </div>
              <textarea
                id="move-parecer"
                className="form-input font-mono text-sm"
                rows={10}
                value={move.parecer}
                onChange={(e) => setMove((f) => ({ ...f, parecer: e.target.value }))}
              />
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={Boolean(move.exigencia)}
                  onChange={(e) => setMove((f) => ({ ...f, exigencia: e.target.checked }))}
                />
                Registrar exigência formal e notificar o requerente
              </label>
            </div>
          </form>
        </FormModalLayout>
      )}
      {analiseExig.item ? (
        <FormModalLayout
          title="Analisar exigência"
          subtitle="Deferir ou indeferir a resposta do requerente."
          onClose={() => setAnaliseExig({ item: null, texto: '', deferida: true })}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!analiseExig.texto.trim() || analisarExigencia.isLoading}
              onClick={() => analisarExigencia.mutate(analiseExig)}
            >
              Registrar análise
            </button>
          }
        >
          <p className="text-sm text-gray-700">{analiseExig.item.resposta_texto}</p>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={analiseExig.deferida}
              onChange={(e) => setAnaliseExig((f) => ({ ...f, deferida: e.target.checked }))}
            />
            Deferir
          </label>
          <textarea
            className="form-input mt-3"
            rows={4}
            placeholder="Parecer da análise"
            value={analiseExig.texto}
            onChange={(e) => setAnaliseExig((f) => ({ ...f, texto: e.target.value }))}
          />
        </FormModalLayout>
      ) : null}
      {respostaExig ? (
        <ExigenciaRespostaModal
          move={respostaExig}
          processoId={id}
          onClose={() => setRespostaExig(null)}
          onAnswered={invalidate}
        />
      ) : null}
      {showHistory ? (
        <RecordHistoryDrawer
          entityType="processes"
          entityId={id}
          title={`Histórico · ${processo.numero || id}`}
          onClose={() => setShowHistory(false)}
        />
      ) : null}
    </FormPageShell>
  );
};

export default ProcessoDetalhe;
